import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useEnableGcpAccount } from "featureFlags/hooks/useEnableGcpAccount";
import { isEngineStatusActive } from "services/engines/engine.types";
import { useWorkspaceEngines } from "services/engines/useWorkspaceEngines";
import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";

import { useAccessManager } from "components/App/accessManager";
import ContextMenuItem from "components/ContextMenu/ContextMenuItem";

import { ContextSelector } from "../ContextSelector";
import { EngineIconWrapper } from "../components/EngineIconWrapper";
import { EngineItemContent } from "../components/EngineItemContent/EngineItemContent";
import { EngineItemInfo } from "../components/EngineItemInfo/EngineItemInfo";
import { EngineStatusIndicator } from "../components/EngineStatusIndicator/EngineStatusIndicator";
import {
  EngineOperateTooltipTitle,
  EngineUsageTooltipTitle,
  SystemEngineTooltipTitle,
} from "../components/tooltipTitles";

import styles from "./EngineContextSelector.module.scss";

type Props = {
  active: boolean;
  isDisabled: boolean;
  tooltipError?: React.ReactNode | string;
  selectedEngineName: string;
  onSelectEngine: (_engineName: string) => void;
  includeSystemEngine?: boolean;
};

export const EngineContextSelector = (props: Props) => {
  const {
    active,
    isDisabled,
    tooltipError,
    selectedEngineName,
    onSelectEngine,
    includeSystemEngine = false,
  } = props;
  const { t } = useTranslation();
  const { isAllowedTo } = useAccessManager();
  const { data: engines = [] } = useWorkspaceEngines({ includeSystemEngine });
  const enabledGcpAccount = useEnableGcpAccount();
  const searchEngineRef = useRef<HTMLDivElement>(null);
  const [engineSelectorOpen, setEngineSelectorOpen] = useState(false);
  const [searchEngine, setSearchEngine] = useState("");

  const selectedEngine = engines.find(
    engine => engine.engineName === selectedEngineName
  );

  const sortedEngines = [...engines].sort((a, b) => {
    if (a.engineName === "system") return -1;
    if (b.engineName === "system") return 1;

    if (!enabledGcpAccount) {
      const aActive = isEngineStatusActive(a.status);
      const bActive = isEngineStatusActive(b.status);

      if (aActive !== bActive) return aActive ? -1 : 1;
    }

    return a.engineName.localeCompare(b.engineName);
  });

  const foundEngines = sortedEngines.filter(engine => {
    return engine.engineName.toLowerCase().includes(searchEngine.toLowerCase());
  });
  return (
    <ContextSelector
      active={active}
      isDisabled={isDisabled}
      selectorRef={searchEngineRef}
      tooltipError={tooltipError}
      onClick={() => {
        if (isDisabled) return;
        setSearchEngine("");
        setEngineSelectorOpen(true);
      }}
      testId="engine-context-selector"
      leftIcon={<EngineIconWrapper engine={selectedEngine} />}
      label={selectedEngineName}
      selectorIsOpen={engineSelectorOpen}
      onClose={() => {
        setEngineSelectorOpen(false);
      }}
      showSearch={engines.length > 5}
      setSearchValue={setSearchEngine}
      searchValue={searchEngine}
      searchTestId="search-engine-context"
      notFoundText={t("workspace.context_selector.no_matching_engine")}
      noSearchResults={foundEngines.length === 0}
      popoverClasses={{
        paper: styles.popoverPaper,
      }}
      items={foundEngines.map(engine => {
        const isSystem = engine.engineName === "system";

        const canOperateEngine = isAllowedTo(
          PrivilegeObjectType.engine,
          engine.engineName,
          SqlRbacAction.OPERATE,
          { owner: engine.engineOwner }
        );

        const canUseEngine = isAllowedTo(
          PrivilegeObjectType.engine,
          engine.engineName,
          SqlRbacAction.USAGE,
          { owner: engine.engineOwner }
        );

        const systemEngineTooltipContent = isSystem ? (
          <SystemEngineTooltipTitle />
        ) : null;
        const operateEngineTooltipContent =
          !canOperateEngine && !enabledGcpAccount ? (
            <EngineOperateTooltipTitle />
          ) : null;
        const usageEngineTooltipContent =
          !canUseEngine && !enabledGcpAccount ? (
            <EngineUsageTooltipTitle />
          ) : null;

        return (
          <ContextMenuItem
            key={engine.engineName}
            checked={engine.engineName === selectedEngineName}
            testId={`context-engine-item-${engine.engineName}`}
            disabled={!canUseEngine}
            onClick={() => {
              onSelectEngine(engine.engineName);
              setEngineSelectorOpen(false);
            }}
            checkedIconPlaceholder={true}
            text={engine.engineName}
            rightAdornment={
              !enabledGcpAccount ? (
                <EngineStatusIndicator engine={engine} />
              ) : null
            }
            classes={{
              textClassName: styles.itemText,
              contentClassName:
                isSystem || !canOperateEngine || !canUseEngine
                  ? styles.itemContent
                  : "",
              checkIconWrapper: styles.itemCheckIconWrapper,
            }}
            contentVisibleOnlyOnHover
            content={
              isSystem || !canOperateEngine || !canUseEngine ? (
                <EngineItemInfo
                  title={
                    systemEngineTooltipContent ||
                    usageEngineTooltipContent ||
                    operateEngineTooltipContent
                  }
                />
              ) : (
                <EngineItemContent engine={engine} />
              )
            }
          />
        );
      })}
    />
  );
};
